import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Menu, X, User } from "lucide-react";
import Logo from "@/components/Logo";
import { fetchConfig } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/picks", label: "Picks", testid: "nav-link-picks" },
  { to: "/results", label: "Results", testid: "nav-link-results" },
  { to: "/how-it-works", label: "How It Works", testid: "nav-link-how-it-works" },
  { to: "/#pricing", label: "Pricing", testid: "nav-link-pricing" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, isVip, vipTestMode } = useAuth();
  const isVipMember = user?.plan === "vip";
  const { data: config } = useQuery({ queryKey: ["config"], queryFn: fetchConfig, staleTime: 300_000 });
  const vipUrl = config?.whop?.vip_checkout_url || "/#pricing";

  const linkCls = ({ isActive }) =>
    `font-display text-sm font-semibold uppercase tracking-wider transition-colors ${
      isActive ? "text-[#00E676]" : "text-slate-300 hover:text-white"
    }`;

  return (
    <header className="sticky top-0 z-50 border-b border-[#1E2738] bg-[#080A0F]/85 backdrop-blur-xl" data-testid="main-nav">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Logo />

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {LINKS.map((l) =>
            l.to.includes("#") ? (
              <Link key={l.to} to={l.to} data-testid={l.testid} className="font-display text-sm font-semibold uppercase tracking-wider text-slate-300 transition-colors hover:text-white">
                {l.label}
              </Link>
            ) : (
              <NavLink key={l.to} to={l.to} data-testid={l.testid} className={linkCls}>
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          {vipTestMode === "vip" && (
            <span data-testid="nav-vip-test-badge" className="tag border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]">
              <User size={10} /> VIP Test Mode
            </span>
          )}
          {user ? (
            <Link
              to="/dashboard"
              data-testid="nav-login-btn"
              className="flex items-center gap-2 rounded-lg border border-[#2A364F] px-4 py-2 font-display text-sm font-semibold uppercase tracking-wider text-slate-200 transition-colors hover:border-[#00E676]/50 hover:text-[#00E676]"
            >
              <User size={14} /> {isVip ? "VIP Member" : "Member"}
            </Link>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login-btn"
              className="rounded-lg px-4 py-2 font-display text-sm font-semibold uppercase tracking-wider text-slate-300 transition-colors hover:text-white"
            >
              Login
            </Link>
          )}
          {isVipMember ? (
            <Link to="/dashboard" data-testid="nav-cta-dashboard" className="btn-primary !px-5 !py-2">
              Dashboard
            </Link>
          ) : (
            <a href={vipUrl} target={vipUrl.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid="nav-cta-vip" className="btn-primary !px-5 !py-2">
              Join VIP
            </a>
          )}
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="rounded-md p-2 text-slate-200 lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="border-t border-[#1E2738] bg-[#0A0D14] px-4 py-4 lg:hidden" data-testid="nav-mobile-menu">
          <div className="flex flex-col gap-1">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`${l.testid}-mobile`}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-3 font-display text-base font-semibold uppercase tracking-wider text-slate-200 hover:bg-[#141A26] hover:text-[#00E676]"
              >
                {l.label}
              </Link>
            ))}
            <Link
              to={user ? "/dashboard" : "/login"}
              data-testid="nav-login-btn-mobile"
              onClick={() => setOpen(false)}
              className="rounded-lg px-3 py-3 font-display text-base font-semibold uppercase tracking-wider text-slate-200 hover:bg-[#141A26]"
            >
              {user ? "Dashboard" : "Login"}
            </Link>
            {isVipMember ? (
              <Link to="/dashboard" data-testid="nav-cta-dashboard-mobile" className="btn-primary mt-2" onClick={() => setOpen(false)}>
                Dashboard
              </Link>
            ) : (
              <a
                href={vipUrl}
                target={vipUrl.startsWith("http") ? "_blank" : undefined}
                rel="noopener noreferrer"
                data-testid="nav-cta-vip-mobile"
                className="btn-primary mt-2"
                onClick={() => setOpen(false)}
              >
                Join VIP
              </a>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export default Navbar;
